import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';

// 개발자전용메뉴 진입용 키 (운영자 실수 방지용, 세션당 1회 입력)
const DEV_MENU_ACCESS_KEY = 'iloveidea';
const SESSION_STORAGE_KEY = 'systemAdminAccessGranted';
import {
  Container,
  Grid,
  Typography,
  Card,
  CardContent,
  Box,
  Button,
  Alert,
  CircularProgress,
  Divider,
  Tabs,
  Tab,
  TextField
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import { Http } from 'api/http/http';
import { logout } from 'store/reducers/auth';

// 업로드 설정 배열
const uploadConfigs = [
  {
    id: 'departments',
    label: '1. 학과 데이터',
    endpoint: '/api/admin/excel/departments',
    description: 'CSV 파일을 선택하여 학과 데이터를 업로드할 수 있습니다.',
    successMessage: '학과 데이터가 성공적으로 업로드되었습니다.'
  },
  {
    id: 'department-codes',
    label: '2. 개설학과 코드표',
    endpoint: '/api/admin/excel/department-codes',
    description: 'CSV 파일을 선택하여 학정번호 앞 4자리 → 개설단과대학/개설학과 코드표를 업데이트할 수 있습니다. (수강기록 업로드 전에 먼저 반영하세요)',
    successMessage: '개설학과 코드표가 성공적으로 업데이트되었습니다.'
  },
  {
    id: 'students',
    label: '3. 수강 기록 (학생 정보)',
    endpoint: '/api/admin/excel/students',
    description: 'CSV 파일을 선택하여 학생 정보를 업데이트할 수 있습니다.',
    successMessage: '학생 정보가 성공적으로 업데이트되었습니다.'
  },
  {
    id: 'modules',
    label: '4. 교과목 Rule (모듈용)',
    endpoint: '/api/admin/excel/modules',
    description: 'CSV 파일을 선택하여 모듈 교과목 규칙을 업데이트할 수 있습니다.',
    successMessage: '모듈 교과목 규칙이 성공적으로 업데이트되었습니다.'
  },
  {
    id: 'standard-courses',
    label: '5. 교과목 Rule (표준교과목용)',
    endpoint: '/api/admin/excel/standard-courses',
    description: 'CSV 파일을 선택하여 표준교과목 규칙을 업데이트할 수 있습니다.',
    successMessage: '표준교과목 규칙이 성공적으로 업데이트되었습니다.'
  },
  {
    id: 'courses',
    label: '6. 교과목 Rule (개설교과목용)',
    endpoint: '/api/admin/excel/courses',
    description: 'CSV 파일을 선택하여 개설교과목 규칙을 업데이트할 수 있습니다.',
    successMessage: '개설교과목 규칙이 성공적으로 업데이트되었습니다.'
  },
  {
    id: 'course-enrollments',
    label: '7. 수강기록 (교과목 이수 내역)',
    endpoint: '/api/admin/excel/course-enrollments',
    description: 'CSV 파일을 선택하여 교과목 이수 내역을 업데이트할 수 있습니다.',
    successMessage: '교과목 이수 내역이 성공적으로 업데이트되었습니다.'
  }
];

// 초기 State 생성
const createInitialUploadStates = () => {
  const states = {};
  uploadConfigs.forEach((config) => {
    states[config.id] = { file: null, uploading: false, result: null, error: null };
  });
  return states;
};

// 재사용 가능한 업로드 섹션 컴포넌트
const UploadSection = ({ config, state, onFileSelect, onUpload, onCancel }) => {
  return (
    <Box sx={{ py: 3 }}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {config.description}
      </Typography>

      {/* 파일 선택 영역 */}
      <Box
        sx={{
          border: '2px dashed #ccc',
          borderRadius: 2,
          p: 3,
          textAlign: 'center',
          backgroundColor: '#fafafa',
          mb: 2
        }}
      >
        <input type="file" accept=".csv" onChange={onFileSelect} style={{ display: 'none' }} id={`csv-file-input-${config.id}`} />
        <label htmlFor={`csv-file-input-${config.id}`}>
          <Button variant="outlined" component="span" startIcon={<CloudUploadIcon />} sx={{ mb: 2 }}>
            CSV 파일 선택
          </Button>
        </label>

        {state.file && (
          <Box sx={{ mt: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
              <InsertDriveFileIcon color="primary" />
              <Typography variant="body1">{state.file.name}</Typography>
              <Typography variant="body2" color="text.secondary">
                ({(state.file.size / 1024).toFixed(2)} KB)
              </Typography>
            </Box>
            <Button variant="text" color="error" size="small" onClick={onCancel} sx={{ mt: 1 }}>
              취소
            </Button>
          </Box>
        )}

        {!state.file && (
          <Typography variant="body2" color="text.secondary">
            파일을 선택해주세요
          </Typography>
        )}
      </Box>

      {/* 업로드 버튼 */}
      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
        <Button
          variant="contained"
          color="primary"
          onClick={onUpload}
          disabled={!state.file || state.uploading}
          startIcon={state.uploading ? <CircularProgress size={20} color="inherit" /> : <CloudUploadIcon />}
          sx={{ minWidth: 150 }}
        >
          {state.uploading ? '업로드 중...' : '업로드'}
        </Button>
      </Box>

      {/* 결과 표시 영역 */}
      {state.error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {state.error}
        </Alert>
      )}

      {state.result && state.result.success && (
        <Alert severity="success" sx={{ mt: 2 }}>
          {state.result.message}
          {state.result.data?.result && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="body2">업로드 결과: {JSON.stringify(state.result.data.result)}</Typography>
            </Box>
          )}
        </Alert>
      )}
    </Box>
  );
};

const SystemAdmin = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // 개발자전용메뉴 키 체크 (세션에 저장된 값이 있으면 통과)
  const [accessGranted, setAccessGranted] = useState(() => sessionStorage.getItem(SESSION_STORAGE_KEY) === 'true');
  const [accessKeyInput, setAccessKeyInput] = useState('');
  const [accessKeyError, setAccessKeyError] = useState('');

  useEffect(() => {
    if (accessGranted) {
      sessionStorage.setItem(SESSION_STORAGE_KEY, 'true');
    }
  }, [accessGranted]);

  // 현재 탭 State
  const [currentTab, setCurrentTab] = useState(0);

  // 각 탭별 업로드 State
  const [uploadStates, setUploadStates] = useState(createInitialUploadStates());

  // 더미 학생 생성 폼 State
  const initialDummyForm = {
    studentNumber: '',
    name: '',
    password: '',
    departmentName: '',
    enrollmentYear: '',
    enrollmentSemester: '',
    completedSemester: ''
  };
  const [dummyForm, setDummyForm] = useState(initialDummyForm);
  const [dummySubmitting, setDummySubmitting] = useState(false);
  const [dummyResult, setDummyResult] = useState(null);
  const [dummyError, setDummyError] = useState(null);

  // 리프레시 토큰 만료 처리
  const handleRefreshExpired = () => {
    navigate('/studentLogin');
  };

  // 로그아웃 처리
  const handleLogout = () => {
    dispatch(logout());
  };

  const http = new Http({
    onRefreshExpired: handleRefreshExpired,
    onLogout: handleLogout
  });

  // 탭 변경 핸들러
  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
  };

  // State 업데이트 헬퍼 함수
  const updateUploadState = (configId, updates) => {
    setUploadStates((prev) => ({
      ...prev,
      [configId]: { ...prev[configId], ...updates }
    }));
  };

  // 파일 선택 핸들러
  const handleFileSelect = (configId) => (event) => {
    const file = event.target.files[0];
    if (file) {
      // CSV 파일 검증
      if (!file.name.endsWith('.csv')) {
        updateUploadState(configId, {
          error: 'CSV 파일만 업로드할 수 있습니다.',
          file: null
        });
        return;
      }
      updateUploadState(configId, {
        file,
        error: null,
        result: null
      });
    }
  };

  // 파일 업로드 핸들러
  const handleUpload = (config) => async () => {
    const state = uploadStates[config.id];
    if (!state.file) {
      updateUploadState(config.id, { error: '파일을 선택해주세요.' });
      return;
    }

    updateUploadState(config.id, { uploading: true, error: null, result: null });

    try {
      const formData = new FormData();
      formData.append('file', state.file);

      const response = await http.postWithToken(config.endpoint, formData);

      updateUploadState(config.id, {
        uploading: false,
        result: {
          success: true,
          message: config.successMessage,
          data: response.data
        },
        file: null
      });

      // input 초기화
      const inputElement = document.getElementById(`csv-file-input-${config.id}`);
      if (inputElement) {
        inputElement.value = '';
      }
    } catch (err) {
      console.error('업로드 실패:', err);
      const errorMessage = err.response?.data?.message || err.message || '파일 업로드에 실패했습니다.';
      updateUploadState(config.id, {
        uploading: false,
        error: errorMessage
      });
    }
  };

  // 파일 선택 취소 핸들러
  const handleCancelFile = (configId) => () => {
    updateUploadState(configId, { file: null, error: null });
    const inputElement = document.getElementById(`csv-file-input-${configId}`);
    if (inputElement) {
      inputElement.value = '';
    }
  };

  // 개발자전용메뉴 키 확인
  const handleAccessKeySubmit = () => {
    setAccessKeyError('');
    if (accessKeyInput.trim() === DEV_MENU_ACCESS_KEY) {
      setAccessGranted(true);
      setAccessKeyInput('');
    } else {
      setAccessKeyError('키가 일치하지 않습니다.');
    }
  };

  // 더미 폼 필드 변경
  const handleDummyFormChange = (field) => (event) => {
    setDummyForm((prev) => ({ ...prev, [field]: event.target.value }));
    setDummyError(null);
    setDummyResult(null);
  };

  // 더미 학생 생성 제출 (클라이언트 검증 + API 호출)
  const handleDummySubmit = async () => {
    setDummyError(null);
    setDummyResult(null);

    const { studentNumber, name, password, departmentName, enrollmentYear, enrollmentSemester, completedSemester } = dummyForm;

    if (!studentNumber?.trim()) {
      setDummyError('학번을 입력하세요.');
      return;
    }
    if (!name?.trim()) {
      setDummyError('이름을 입력하세요.');
      return;
    }
    if (!password?.trim()) {
      setDummyError('비밀번호를 입력하세요.');
      return;
    }
    if (!departmentName?.trim()) {
      setDummyError('학과명을 입력하세요.');
      return;
    }
    if (!enrollmentYear?.trim()) {
      setDummyError('입학년도를 입력하세요.');
      return;
    }
    if (!enrollmentSemester?.trim()) {
      setDummyError('입학학기를 입력하세요.');
      return;
    }
    if (!completedSemester?.trim()) {
      setDummyError('이수학기를 입력하세요.');
      return;
    }

    const numYear = Number(enrollmentYear);
    const numEnrollSem = Number(enrollmentSemester);
    const numCompletedSem = Number(completedSemester);
    if (Number.isNaN(numYear) || !Number.isInteger(numYear)) {
      setDummyError('입학년도는 숫자로 입력하세요.');
      return;
    }
    if (Number.isNaN(numEnrollSem) || !Number.isInteger(numEnrollSem)) {
      setDummyError('입학학기는 숫자로 입력하세요.');
      return;
    }
    if (Number.isNaN(numCompletedSem) || !Number.isInteger(numCompletedSem)) {
      setDummyError('이수학기는 숫자로 입력하세요.');
      return;
    }

    setDummySubmitting(true);
    try {
      await http.postWithToken('/api/admin/dummy/student/all', {
        studentNumber: studentNumber.trim(),
        name: name.trim(),
        password: password.trim(),
        departmentName: departmentName.trim(),
        enrollmentYear: enrollmentYear.trim(),
        enrollmentSemester: enrollmentSemester.trim(),
        completedSemester: completedSemester.trim()
      });
      setDummyResult({ success: true, message: '더미 학생 계정이 생성되었습니다.' });
      setDummyForm(initialDummyForm);
    } catch (err) {
      console.error('더미 학생 생성 실패:', err);
      const message = err.response?.data?.message || err.response?.data?.error || err.message || '더미 학생 생성에 실패했습니다.';
      setDummyError(message);
    } finally {
      setDummySubmitting(false);
    }
  };

  // 키 미입력 시 진입 키 입력 화면
  if (!accessGranted) {
    return (
      <Container>
        <Grid container justifyContent="center" alignItems="center" sx={{ minHeight: '50vh' }}>
          <Grid item xs={12} sm={8} md={6}>
            <Card>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  개발자전용메뉴
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  진입하려면 키를 입력하세요.
                </Typography>
                <TextField
                  fullWidth
                  type="password"
                  label="키"
                  value={accessKeyInput}
                  onChange={(e) => {
                    setAccessKeyInput(e.target.value);
                    setAccessKeyError('');
                  }}
                  onKeyDown={(e) => e.key === 'Enter' && handleAccessKeySubmit()}
                  error={!!accessKeyError}
                  helperText={accessKeyError}
                  sx={{ mb: 2 }}
                />
                <Button variant="contained" color="primary" onClick={handleAccessKeySubmit}>
                  확인
                </Button>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>
    );
  }

  return (
    <Container>
      <Grid item xs={12}>
        <Grid container justifyContent="space-around" alignItems="center" rowSpacing={4.5} columnSpacing={2.75}>
          <Grid item xs={11} sm={11} md={11} lg={11}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <Typography variant="h4">시스템 관리자 (개발자전용)</Typography>
                </Box>

                <Divider sx={{ mb: 3 }} />

                {/* Tabs */}
                <Tabs
                  value={currentTab}
                  onChange={handleTabChange}
                  variant="scrollable"
                  scrollButtons="auto"
                  sx={{ borderBottom: 1, borderColor: 'divider' }}
                >
                  {uploadConfigs.map((config) => (
                    <Tab key={config.id} label={config.label} />
                  ))}
                </Tabs>

                {/* Tab Panels */}
                {uploadConfigs.map((config, index) => (
                  <Box key={config.id} hidden={currentTab !== index} role="tabpanel">
                    {currentTab === index && (
                      <UploadSection
                        config={config}
                        state={uploadStates[config.id]}
                        onFileSelect={handleFileSelect(config.id)}
                        onUpload={handleUpload(config)}
                        onCancel={handleCancelFile(config.id)}
                      />
                    )}
                  </Box>
                ))}

                {/* 더미 학생 생성 섹션 */}
                <Divider sx={{ my: 3 }} />
                <Typography variant="h6" sx={{ mb: 2 }}>
                  더미 학생 생성
                </Typography>
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="학번"
                      value={dummyForm.studentNumber}
                      onChange={handleDummyFormChange('studentNumber')}
                      placeholder="1111111111"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="이름"
                      value={dummyForm.name}
                      onChange={handleDummyFormChange('name')}
                      placeholder="더미학생1"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      type="password"
                      label="비밀번호"
                      value={dummyForm.password}
                      onChange={handleDummyFormChange('password')}
                      placeholder="test1234"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="학과명"
                      value={dummyForm.departmentName}
                      onChange={handleDummyFormChange('departmentName')}
                      placeholder="정보융합학부"
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      label="입학년도"
                      value={dummyForm.enrollmentYear}
                      onChange={handleDummyFormChange('enrollmentYear')}
                      placeholder="2024"
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      label="입학학기"
                      value={dummyForm.enrollmentSemester}
                      onChange={handleDummyFormChange('enrollmentSemester')}
                      placeholder="1"
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      label="이수학기"
                      value={dummyForm.completedSemester}
                      onChange={handleDummyFormChange('completedSemester')}
                      placeholder="7"
                    />
                  </Grid>
                </Grid>
                <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 2 }}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleDummySubmit}
                    disabled={dummySubmitting}
                    startIcon={dummySubmitting ? <CircularProgress size={20} color="inherit" /> : null}
                  >
                    {dummySubmitting ? '생성 중...' : '더미 계정 만들기'}
                  </Button>
                </Box>
                {dummyError && (
                  <Alert severity="error" sx={{ mt: 2 }}>
                    {dummyError}
                  </Alert>
                )}
                {dummyResult && dummyResult.success && (
                  <Alert severity="success" sx={{ mt: 2 }}>
                    {dummyResult.message}
                  </Alert>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Grid>
    </Container>
  );
};

export default SystemAdmin;
